import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import WhatsAppBooking from '../components/WhatsAppBooking';
import SEOJsonLd from '../../../components/base/SEOJsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://thesunshinehealing.com';

const studioImage = 'https://public.readdy.ai/ai/img_res/561a5639c2d8c3dc8e54df329358952e.jpg spiritual healing studio banner design with glowing lotus flowers chakra rainbow energy light sacred geometry golden patterns serene meditation space white and gold cream color palette peaceful divine atmosphere ethereal glow natural elements Singapore wellness brand beautiful harmonious spiritual landscape with soft light rays and healing crystals&width=1200&height=630&seq=studio-og-preview-001&orientation=landscape';

const bachJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Bach Flower Remedies Singapore — Natural Emotional Healing 巴赫花精疗愈',
    description:
      'Bach Flower Remedies in Singapore. Natural vibrational healing using 38 flower essences to harmonize anxiety, fear, depression and emotional imbalance at the root. Personalized formula. Sessions from SGD $60.',
    url: `${siteUrl}/services/bach`,
    image: studioImage,
    areaServed: 'Singapore',
    provider: {
      '@type': 'LocalBusiness',
      name: 'Sunshine Healing Studio',
      url: siteUrl,
      telephone: '+6593651286',
      address: { '@type': 'PostalAddress', addressLocality: 'Singapore', addressCountry: 'SG' },
    },
    offers: [
      { '@type': 'Offer', price: '120', priceCurrency: 'SGD', description: 'Emotional Assessment + Formula (60 mins)' },
      { '@type': 'Offer', price: '60', priceCurrency: 'SGD', description: 'Follow-up Adjustment (30 mins)' },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页 Home', item: `${siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: '巴赫花精疗愈 Bach Flower Remedies', item: `${siteUrl}/services/bach` },
    ],
  },
];

export default function BachPage() {
  const { t } = useTranslation('common');

  return (
    <div className="min-h-screen bg-white">
      <title>巴赫花精疗愈 新加坡 | Bach Flower Remedies Singapore — Natural Emotional Healing</title>
      <meta name="description" content="新加坡巴赫花精疗愈，运用38种天然花精振动能量，精准调和焦虑、恐惧、抑郁等负面情绪，从根源疗愈情绪失衡。个性化配方，SGD $60起。Singapore Bach Flower Remedies — 38 essences, emotional healing, anxiety, depression." />
      <meta name="keywords" content="Bach flower remedies Singapore, emotional healing Singapore, flower essence therapy, anxiety healing, natural therapy Singapore" />
      <link rel="canonical" href={`${siteUrl}/services/bach`} />
      <meta property="og:title" content="巴赫花精疗愈 新加坡 | Bach Flower Remedies Singapore" />
      <meta property="og:description" content="38种天然花精，温和调和负面情绪，从根源疗愈情绪失衡。新加坡巴赫花精疗愈，个性化配方，安全温和无副作用。" />
      <meta property="og:url" content={`${siteUrl}/services/bach`} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={studioImage} />
      <SEOJsonLd id="bach-jsonld" data={bachJsonLd} />

      <Navbar />

      {/* Hero Banner */}
      <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center overflow-hidden pt-16 md:pt-0">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=beautiful%20wildflowers%20in%20morning%20dew%20with%20soft%20pink%20and%20rose%20colored%20light%2C%20natural%20flower%20essence%20healing%20energy%2C%20delicate%20botanical%20garden%20scene%2C%20gentle%20emotional%20healing%20atmosphere%2C%20pure%20and%20natural%20environment%2C%20serene%20floral%20landscape%20with%20ethereal%20glow&width=1920&height=1080&seq=bach-hero-bg&orientation=landscape"
            alt="Bach Flower Remedies"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-rose-900/60 via-rose-900/40 to-rose-900/60"></div>
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-6 text-center">
          <div className="inline-block px-4 py-1.5 bg-rose-500/20 backdrop-blur-sm rounded-full border border-rose-400/30 mb-4 md:mb-5">
            <span className="text-rose-300 text-xs md:text-sm font-medium tracking-widest">SPIRITUAL HEALING</span>
          </div>
          <h1 className="text-3xl md:text-6xl font-bold text-white mb-4 md:mb-5 tracking-tight">
            {t('bachDetail.hero.title')}
          </h1>
          <p className="text-base md:text-xl text-stone-200 leading-relaxed">
            {t('bachDetail.hero.description')}
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 md:py-24 px-4 md:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:grid md:grid-cols-2 md:gap-16 md:items-center gap-8">
            <div>
              <p className="text-rose-600 text-xs md:text-sm font-semibold tracking-widest uppercase mb-2 md:mb-3">Introduction</p>
              <h2 className="text-2xl md:text-4xl font-bold text-stone-800 mb-4 md:mb-6 leading-snug">
                {t('bachDetail.intro.title')}
              </h2>
              <p className="text-stone-600 leading-relaxed text-sm md:text-base">
                {t('bachDetail.intro.content')}
              </p>
              <div className="mt-6 md:mt-10 grid grid-cols-2 gap-3 md:gap-4">
                {[
                  { icon: 'ri-emotion-happy-line', label: t('bachDetail.benefits.benefit1.title') },
                  { icon: 'ri-leaf-line', label: t('bachDetail.benefits.benefit2.title') },
                  { icon: 'ri-seedling-line', label: t('bachDetail.benefits.benefit3.title') },
                  { icon: 'ri-shield-check-line', label: t('bachDetail.benefits.benefit6.title') },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 md:gap-3 bg-rose-50 rounded-xl px-3 md:px-4 py-2.5 md:py-3">
                    <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-rose-600 flex-shrink-0">
                      <i className={`${item.icon} text-base md:text-lg`}></i>
                    </div>
                    <span className="text-stone-700 text-xs md:text-sm font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative w-full h-64 md:h-[520px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://static.readdy.ai/image/705f25322c737a89d377410a2840897b/6610fc2df0ab6dd4b291687013da17b0.png"
                alt="Bach Flower Essences"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Suitable For & Method */}
      <section className="py-12 md:py-20 px-4 md:px-6 bg-rose-50/60">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:grid md:grid-cols-2 md:gap-16 gap-10">
            {/* 适合人群 */}
            <div>
              <div className="flex items-center gap-3 mb-5 md:mb-7">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-rose-100 text-rose-600">
                  <i className="ri-user-heart-line text-lg"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-stone-800">{t('bachDetail.suitable.title')}</h3>
              </div>
              <ul className="space-y-3">
                {[
                  t('bachDetail.suitable.item1'),
                  t('bachDetail.suitable.item2'),
                  t('bachDetail.suitable.item3'),
                  t('bachDetail.suitable.item4'),
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-stone-600 text-sm md:text-base">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-rose-500 text-base"></i>
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* 疗愈方式 */}
            <div>
              <div className="flex items-center gap-3 mb-5 md:mb-7">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-rose-100 text-rose-600">
                  <i className="ri-magic-line text-lg"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-stone-800">{t('bachDetail.method.title')}</h3>
              </div>
              <div className="space-y-4">
                {[
                  { icon: 'ri-chat-smile-2-line', title: t('bachDetail.method.step1.title'), desc: t('bachDetail.method.step1.desc') },
                  { icon: 'ri-flask-line', title: t('bachDetail.method.step2.title'), desc: t('bachDetail.method.step2.desc') },
                  { icon: 'ri-calendar-check-line', title: t('bachDetail.method.step3.title'), desc: t('bachDetail.method.step3.desc') },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 p-4 bg-white rounded-xl shadow-sm border border-rose-100">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-rose-100 text-rose-600 flex-shrink-0">
                      <i className={`${item.icon} text-base`}></i>
                    </div>
                    <div>
                      <p className="font-semibold text-stone-800 text-sm md:text-base mb-1">{item.title}</p>
                      <p className="text-stone-500 text-xs md:text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-12 md:py-20 px-4 md:px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-rose-100 text-rose-600">
              <i className="ri-price-tag-3-line text-lg"></i>
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-stone-800">{t('bachDetail.pricing.title')}</h3>
          </div>
          <p className="text-stone-400 text-sm mb-8">{t('bachDetail.pricing.note')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {(t('bachDetail.pricing.items', { returnObjects: true }) as { duration: string; price: string; label: string }[]).map((item, i) => (
              <div key={i} className="flex-1 max-w-xs mx-auto sm:mx-0 bg-rose-50 border border-rose-100 rounded-2xl px-8 py-7 flex flex-col items-center gap-2 shadow-sm">
                <span className="text-xs font-semibold tracking-widest text-rose-500 uppercase mb-1">{item.label}</span>
                <div className="flex items-center gap-2 text-stone-700">
                  <div className="w-5 h-5 flex items-center justify-center text-rose-400">
                    <i className="ri-time-line text-base"></i>
                  </div>
                  <span className="text-base font-medium">{item.duration}</span>
                </div>
                <div className="text-2xl md:text-3xl font-bold text-rose-600 mt-1">{item.price}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking */}
      <WhatsAppBooking accentColor="rose" serviceName="巴赫花精疗愈" />

      {/* Back */}
      <section className="py-8 md:py-10 px-4 md:px-6 text-center bg-white">
        <Link
          to="/#services"
          className="inline-flex items-center gap-2 px-6 md:px-7 py-3 border border-stone-300 text-stone-700 rounded-full hover:bg-stone-100 transition-colors duration-300 text-sm whitespace-nowrap cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          <span>{t('bachDetail.cta.back')}</span>
        </Link>
      </section>

      <Footer />
    </div>
  );
}
